import type { FC, ReactNode } from 'react'
import type { Plan } from '@/types'

import { Check, Pencil } from 'lucide-react'
import { formatNumber, formatPercent, formatRupiah, formatRupiahCompact } from '@/lib/format'
import { Button } from '@/components/ui/button'

interface PlanCardProps {
    plan: Plan
    customers: number
    mrr: number
    share: number
    onEditPrice: (plan: Plan) => void
}

export const PlanCard: FC<PlanCardProps> = ({ plan, customers, mrr, share, onEditPrice }): ReactNode => {
    return (
        <article className='flex min-w-0 flex-col rounded-xl border bg-card p-5 shadow-[0_1px_2px_0_rgb(28_25_23/0.04)]'>
            <div className='flex items-start justify-between gap-3'>
                <div className='min-w-0'>
                    <h3 className='flex items-center gap-2 font-heading text-base font-semibold text-foreground'>
                        <span className='size-2.5 rounded-[3px]' style={{ background: plan.color }} aria-hidden='true' />
                        {plan.name}
                    </h3>
                    <p className='mt-1 text-xs leading-relaxed text-muted-foreground'>
                        {plan.description}
                    </p>
                </div>

                <Button variant='ghost' size='icon-sm' onClick={() => onEditPrice(plan)} aria-label={`Ubah harga paket ${plan.name}`}>
                    <Pencil />
                </Button>
            </div>

            <p className='mt-4 font-heading text-2xl font-semibold tracking-tight text-foreground tabular-nums'>
                {plan.id === 'enterprise' && <span className='mr-1 text-sm font-medium text-muted-foreground'>mulai</span>}
                {formatRupiah(plan.price)}
                <span className='ml-1 text-sm font-medium text-muted-foreground'>/ bulan</span>
            </p>
            <p className='mt-1 text-xs text-muted-foreground tabular-nums'>
                {formatRupiah(plan.annualPrice)} per bulan jika dibayar tahunan
            </p>

            <dl className='mt-5 grid grid-cols-2 gap-3 border-t pt-4'>
                <div>
                    <dt className='text-xs text-muted-foreground'>Pelanggan</dt>
                    <dd className='mt-0.5 text-sm font-semibold text-foreground tabular-nums'>{formatNumber(customers)}</dd>
                </div>
                <div>
                    <dt className='text-xs text-muted-foreground'>MRR</dt>
                    <dd className='mt-0.5 text-sm font-semibold text-foreground tabular-nums'>{formatRupiahCompact(mrr)}</dd>
                </div>
            </dl>

            <div className='mt-3'>
                <div className='flex items-center justify-between text-xs text-muted-foreground'>
                    <span>Porsi dari total MRR</span>
                    <span className='font-medium text-foreground tabular-nums'>{formatPercent(share)}</span>
                </div>
                <div className='mt-1.5 h-1.5 overflow-hidden rounded-r-[4px] bg-muted' aria-hidden='true'>
                    <div className='h-full rounded-r-[4px]' style={{ width: `${share}%`, background: plan.color }} />
                </div>
            </div>

            <ul className='mt-5 space-y-2 text-sm'>
                {plan.features.map((feature) => (
                    <li key={feature} className='flex items-start gap-2 text-muted-foreground'>
                        <Check className='mt-0.5 size-4 shrink-0 text-foreground' aria-hidden='true' />
                        {feature}
                    </li>
                ))}
            </ul>
        </article>
    )
}
